// Public Accept Invitation Page — V1 invite-only (Priority 7 Day 4).
//
// URL: /accept/:token (PUBLIC — no auth required)
//
// Per `planning/plans/onboarding-spec.md` §3 screens 6-10:
//   6. Welcome screen + password set form (token valid)
//   7. Token Expired (410 expired)
//   8. Token Revoked (410 revoked)
//   9. Token Already Used (409 already_accepted) → redirect-to-login affordance
//  10. Token Invalid (404)
//
// V1 simulation:
//   • Token validation via GET /invitations/by-token/:token
//   • Password set via POST /invitations/by-token/:token/accept
//   • Mock JWT returned (real auth = Stage 5 resume)
//   • Successful accept → /onboarding/welcome (Day 5 first-login tour)

import { useState } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Mail,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
} from 'lucide-react'
import { API_BASE, USE_REAL_API } from '../../lib/api'
import { Card } from '../../components/ui/card'
import {
  ORG_TYPE_LABELS,
  SCOPE_LABELS,
  type OrgScope,
  type OrgTypeCode,
} from '../../lib/org-type-labels'
import { useT } from '../../i18n'
import { ACCEPT_INVITATION_STRINGS } from '../../i18n/onboarding/accept-invitation'
import { formatDate } from '../../lib/datetime'

// ─── API ──────────────────────────────────────────────────────────────────────

type InvitationContext = {
  email: string
  org_name: string
  org_scope: OrgScope
  org_type: OrgTypeCode
  initial_tier: string
  personal_note: string | null
  expires_at: string
}

type ValidationError = {
  error: string
  invitation_id?: string
}

type ValidationResult =
  | { kind: 'ok'; context: InvitationContext }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'already_accepted' }
  | { kind: 'invalid' }

async function validateToken(token: string): Promise<ValidationResult> {
  const res = await fetch(`${API_BASE}/invitations/by-token/${encodeURIComponent(token)}`)
  if (res.status === 404) return { kind: 'invalid' }
  if (res.status === 410) {
    const body = (await res.json().catch(() => ({}))) as ValidationError
    if (body.error === 'revoked') return { kind: 'revoked' }
    return { kind: 'expired' }
  }
  if (res.status === 409) return { kind: 'already_accepted' }
  if (!res.ok) throw new Error(`validation ${res.status}`)
  const json = (await res.json()) as { invitation: InvitationContext }
  return { kind: 'ok', context: json.invitation }
}

type AcceptResponse = {
  invitation: { id: string }
  org_id: string
  user_id: string
  mock_token: string
}

async function acceptInvitation(
  token: string,
  body: { password: string; accepted_tos: boolean },
): Promise<AcceptResponse> {
  const res = await fetch(
    `${API_BASE}/invitations/by-token/${encodeURIComponent(token)}/accept`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'failed' }))
    throw new Error(err.error ?? `accept ${res.status}`)
  }
  return res.json()
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Wraps each given variable substring in <strong> within the source phrase.
// Variables are matched by literal occurrence in the order they appear in
// `phrase`. Used to bold ${orgName} + ${role} inside an EN or TR translation
// without forcing the dict to embed JSX.
function interpolateBold(
  phrase: string,
  variables: string[],
): React.ReactNode {
  if (variables.length === 0) return phrase
  // Find variable occurrences and build a sequence of plain + <strong> nodes.
  type Span = { kind: 'text' | 'strong'; value: string }
  const spans: Span[] = [{ kind: 'text', value: phrase }]
  for (const v of variables) {
    if (!v) continue
    for (let i = 0; i < spans.length; i++) {
      const span = spans[i]
      if (span.kind !== 'text') continue
      const idx = span.value.indexOf(v)
      if (idx === -1) continue
      const before = span.value.slice(0, idx)
      const after = span.value.slice(idx + v.length)
      const replacement: Span[] = [
        { kind: 'text', value: before },
        { kind: 'strong', value: v },
        { kind: 'text', value: after },
      ]
      spans.splice(i, 1, ...replacement)
      break
    }
  }
  return spans
    .filter((s) => s.value.length > 0)
    .map((s, i) =>
      s.kind === 'strong' ? <strong key={i}>{s.value}</strong> : (
        <span key={i}>{s.value}</span>
      ),
    )
}

// ─── Layout shell ─────────────────────────────────────────────────────────────

function PublicShell({ children }: { children: React.ReactNode }) {
  const t = useT(ACCEPT_INVITATION_STRINGS)
  return (
    <div className="pex-invitation-shell flex-1 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[var(--color-info)]">
            <Mail size={20} aria-hidden="true" />
            <span className="font-semibold text-lg tracking-tight">{t.brand}</span>
          </div>
          <p className="text-[11px] text-[var(--color-text-muted)] mt-1">
            {t.brandTagline}
          </p>
        </div>
        {children}
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AcceptInvitationPage() {
  const { token } = useParams({ strict: false }) as { token: string }

  // Real-engine mode: the backend has no by-token validation/peek endpoint
  // (the token is single-use and only consumed by POST /onboarding/accept), so
  // there is no invitation context to pre-render. Show a direct account-setup
  // form and surface error states from the accept response. Maket mode keeps
  // the rich context flow below (MSW serves the by-token validation).
  if (USE_REAL_API) {
    return (
      <PublicShell>
        <RealModeAcceptForm token={token} />
      </PublicShell>
    )
  }

  return <MaketAcceptFlow token={token} />
}

function MaketAcceptFlow({ token }: { token: string }) {
  const t = useT(ACCEPT_INVITATION_STRINGS)
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-invitation', token],
    queryFn: () => validateToken(token),
    enabled: !!token,
    retry: false,
  })

  if (isLoading) {
    return (
      <PublicShell>
        <Card className="p-8 text-center">
          <Clock size={32} className="text-[var(--color-text-muted)] mx-auto mb-3 animate-pulse" aria-hidden="true" />
          <p className="text-sm text-[var(--color-text-muted)]">{t.loadingState.message}</p>
        </Card>
      </PublicShell>
    )
  }

  if (error) {
    return (
      <PublicShell>
        <ErrorCard
          icon={AlertCircle}
          tone="rose"
          title={t.errorStates.genericTitle}
          message={t.errorStates.genericMessage(String(error))}
        />
      </PublicShell>
    )
  }

  if (!data) return null

  if (data.kind === 'invalid') {
    return (
      <PublicShell>
        <ErrorCard
          icon={XCircle}
          tone="rose"
          title={t.errorStates.invalidTitle}
          message={t.errorStates.invalidMessage}
        />
      </PublicShell>
    )
  }

  if (data.kind === 'expired') {
    return (
      <PublicShell>
        <ErrorCard
          icon={Clock}
          tone="amber"
          title={t.errorStates.expiredTitle}
          message={t.errorStates.expiredMessage}
        />
      </PublicShell>
    )
  }

  if (data.kind === 'revoked') {
    return (
      <PublicShell>
        <ErrorCard
          icon={XCircle}
          tone="rose"
          title={t.errorStates.revokedTitle}
          message={t.errorStates.revokedMessage}
        />
      </PublicShell>
    )
  }

  if (data.kind === 'already_accepted') {
    return (
      <PublicShell>
        <Card className="pex-invitation-status-card p-6 text-center space-y-3 bg-emerald-50/40 border-emerald-200">
          <CheckCircle2 size={36} className="text-emerald-600 mx-auto" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-[var(--color-text)]">{t.alreadyAccepted.title}</h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {t.alreadyAccepted.description}
          </p>
          <div className="pt-2">
            {/* BUG-17 fix: no real /login page exists in V1; landing page is the
                stable PEXIFY entrypoint. Real auth at M3 ships /login surface. */}
            <Link
              to="/"
              className="text-xs text-[var(--color-info)] hover:underline inline-flex items-center gap-1"
            >
              {t.alreadyAccepted.homeLink}
              <ArrowRight size={11} aria-hidden="true" />
            </Link>
          </div>
        </Card>
      </PublicShell>
    )
  }

  // kind === 'ok' → render welcome + form
  return (
    <PublicShell>
      <WelcomeAndForm token={token} context={data.context} />
    </PublicShell>
  )
}

// ─── Real-engine accept form ──────────────────────────────────────────────────

class AcceptError extends Error {
  status: number
  code?: string
  constructor(status: number, code?: string) {
    super(code ?? `accept_${status}`)
    this.name = 'AcceptError'
    this.status = status
    this.code = code
  }
}

function RealModeAcceptForm({ token }: { token: string }) {
  const t = useT(ACCEPT_INVITATION_STRINGS)
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/onboarding/accept`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          display_name: displayName.trim(),
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          detail?: { code?: string }
        }
        throw new AcceptError(res.status, body.detail?.code)
      }
      return res.json()
    },
    onSuccess: () => navigate({ to: '/login' }),
  })

  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordMismatch = confirm.length > 0 && password !== confirm
  const formValid =
    displayName.trim().length > 0 && password.length >= 8 && password === confirm

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    if (!formValid) return
    mutation.mutate()
  }

  const e = t.realMode.errors
  let errorMessage: string | null = null
  if (mutation.error instanceof AcceptError) {
    const err = mutation.error
    if (err.status === 410) errorMessage = err.code === 'invitation_revoked' ? e.revoked : e.expired
    else if (err.status === 404) errorMessage = e.invalid
    else if (err.status === 409) errorMessage = e.alreadyUsed
    else if (err.status === 403) errorMessage = e.forbidden
    else if (err.status === 429) errorMessage = e.rateLimited
    else errorMessage = e.generic(err.code ?? String(err.status))
  } else if (mutation.error) {
    errorMessage = e.generic(String(mutation.error))
  }

  return (
    <Card className="p-6 space-y-5">
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-[var(--color-text)]">{t.realMode.title}</h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t.realMode.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
            {t.realMode.displayNameLabel}
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            maxLength={120}
            placeholder={t.realMode.displayNamePlaceholder}
            className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-focus)]"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
            {t.welcomeForm.passwordLabel}
          </label>
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              aria-hidden="true"
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={t.welcomeForm.passwordPlaceholder}
              minLength={8}
              className="w-full pl-8 pr-9 py-2 text-sm border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-focus)]"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-0.5"
              aria-label={
                showPassword ? t.welcomeForm.hidePasswordAria : t.welcomeForm.showPasswordAria
              }
            >
              {showPassword ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
            </button>
          </div>
          {passwordTooShort && (
            <p className="text-[11px] text-[var(--color-danger)] mt-1">{t.welcomeForm.passwordTooShort(8)}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
            {t.welcomeForm.confirmPasswordLabel}
          </label>
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              aria-hidden="true"
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder={t.welcomeForm.confirmPasswordPlaceholder}
              minLength={8}
              className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-focus)]"
              required
            />
          </div>
          {passwordMismatch && (
            <p className="text-[11px] text-[var(--color-danger)] mt-1">{t.welcomeForm.passwordMismatch}</p>
          )}
        </div>

        {errorMessage && (
          <div className="p-2 bg-[var(--auth-feedback-bg)] border border-[var(--auth-feedback-border)] rounded text-xs text-[var(--color-danger)] flex items-start gap-1.5" role="alert">
            <AlertCircle size={11} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{errorMessage}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {mutation.isPending ? (
            <>{t.realMode.submitting}</>
          ) : (
            <>
              {t.realMode.submitButton}
              <ArrowRight size={14} aria-hidden="true" />
            </>
          )}
        </button>
      </form>
    </Card>
  )
}

// ─── Welcome + accept form ────────────────────────────────────────────────────

function WelcomeAndForm({ token, context }: { token: string; context: InvitationContext }) {
  const t = useT(ACCEPT_INVITATION_STRINGS)
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [acceptedTos, setAcceptedTos] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      acceptInvitation(token, { password, accepted_tos: acceptedTos }),
    onSuccess: (response) => {
      // BUG-11 fix: store welcome context in sessionStorage (vs URL search
      // params) so email/org_name are not leaked via URL bar, browser history,
      // analytics URL tracking, or shared links. V1 simulation only —
      // real-auth at M3 will provide session-based context.
      try {
        sessionStorage.setItem(
          'pexify_welcome_context',
          JSON.stringify({
            org_type: context.org_type,
            org_name: context.org_name,
            org_scope: context.org_scope,
            email: context.email,
            // Include the just-issued mock user/org ids so the welcome page
            // can later resolve the new identity (BUG-13 follow-up).
            user_id: response.user_id,
            org_id: response.org_id,
            issued_at: new Date().toISOString(),
          }),
        )
      } catch {
        // sessionStorage may be unavailable (private browsing, quota); welcome
        // page falls back to "You're in!" generic state in that case.
      }
      navigate({ to: '/onboarding/welcome' })
    },
  })

  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordMismatch = confirm.length > 0 && password !== confirm
  const formValid =
    password.length >= 8 && password === confirm && acceptedTos

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    if (!formValid) return
    mutation.mutate()
  }

  return (
    <Card className="p-6 space-y-5">
      {/* Welcome header */}
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-[var(--color-text)]">
          {t.welcomeForm.title}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">
          {/* invitedToJoin returns plain text; org_name + role bold via
              interpolateBold helper preserving variable position across
              EN / TR phrasings */}
          {interpolateBold(
            t.welcomeForm.invitedToJoin(
              context.org_name,
              ORG_TYPE_LABELS[context.org_type],
            ),
            [context.org_name, ORG_TYPE_LABELS[context.org_type]],
          )}
        </p>
        <div className="text-xs text-[var(--color-text-muted)] flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--color-surface-muted)] rounded">
            <Mail size={10} aria-hidden="true" />
            {context.email}
          </span>
          <span className="text-[var(--color-text-muted)]">·</span>
          <span>{SCOPE_LABELS[context.org_scope]}</span>
          <span className="text-[var(--color-text-muted)]">·</span>
          <span className="text-amber-700 inline-flex items-center gap-1">
            <Clock size={10} aria-hidden="true" />
            {t.welcomeForm.expiresAt(formatDate(context.expires_at))}
          </span>
        </div>
      </div>

      {/* Personal note */}
      {context.personal_note && (
        <div className="border-l-2 border-[var(--invitation-quote-border)] pl-3 py-1 text-sm italic text-[var(--color-text-muted)] bg-[var(--invitation-quote-bg)] rounded-r">
          "{context.personal_note}"
        </div>
      )}

      {/* Password form */}
      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
            {t.welcomeForm.passwordLabel}
          </label>
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              aria-hidden="true"
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={t.welcomeForm.passwordPlaceholder}
              minLength={8}
              className="w-full pl-8 pr-9 py-2 text-sm border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-focus)]"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-0.5"
              aria-label={
                showPassword
                  ? t.welcomeForm.hidePasswordAria
                  : t.welcomeForm.showPasswordAria
              }
            >
              {showPassword ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
            </button>
          </div>
          {passwordTooShort && (
            <p className="text-[11px] text-[var(--color-danger)] mt-1">
              {t.welcomeForm.passwordTooShort(8)}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--color-text)] mb-1">
            {t.welcomeForm.confirmPasswordLabel}
          </label>
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              aria-hidden="true"
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder={t.welcomeForm.confirmPasswordPlaceholder}
              minLength={8}
              className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-focus)]"
              required
            />
          </div>
          {passwordMismatch && (
            <p className="text-[11px] text-[var(--color-danger)] mt-1">{t.welcomeForm.passwordMismatch}</p>
          )}
        </div>

        <div>
          <label className="flex items-start gap-2 text-xs text-[var(--color-text)] cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTos}
              onChange={(e) => setAcceptedTos(e.target.checked)}
              className="mt-0.5 w-3.5 h-3.5 text-[var(--color-info)] rounded border-[var(--color-border-strong)] focus:ring-indigo-500"
            />
            <span>
              {t.welcomeForm.termsAccept}{' '}
              <span className="text-[var(--color-info)] underline">{t.welcomeForm.termsLink}</span>
              {' & '}
              <span className="text-[var(--color-info)] underline">{t.welcomeForm.privacyLink}</span>.
            </span>
          </label>
          {submitted && !acceptedTos && (
            <p className="text-[11px] text-[var(--color-danger)] mt-1 ml-5">
              {t.welcomeForm.termsRequired}
            </p>
          )}
        </div>

        {mutation.error && (
          <div className="p-2 bg-[var(--auth-feedback-bg)] border border-[var(--auth-feedback-border)] rounded text-xs text-[var(--color-danger)] flex items-start gap-1.5" role="alert">
            <AlertCircle size={11} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{String(mutation.error)}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {mutation.isPending ? (
            <>{t.welcomeForm.activatingInProgress}</>
          ) : (
            <>
              {t.welcomeForm.activateButton}
              <ArrowRight size={14} aria-hidden="true" />
            </>
          )}
        </button>

        <p className="text-[11px] text-[var(--color-text-muted)] text-center pt-1">
          {interpolateBold(
            t.welcomeForm.activateFooter(context.org_name),
            [context.org_name],
          )}
        </p>
      </form>
    </Card>
  )
}

// ─── Error card ───────────────────────────────────────────────────────────────

function ErrorCard({
  icon: Icon,
  tone,
  title,
  message,
}: {
  icon: typeof AlertTriangle
  tone: 'rose' | 'amber'
  title: string
  message: string
}) {
  const t = useT(ACCEPT_INVITATION_STRINGS)
  const toneClass =
    tone === 'rose'
      ? 'bg-rose-50/40 border-rose-200'
      : 'bg-amber-50/40 border-amber-200'
  const iconClass = tone === 'rose' ? 'text-[var(--color-danger)]' : 'text-amber-600'
  return (
    <Card className={`pex-invitation-status-card p-6 text-center space-y-3 ${toneClass}`}>
      <Icon size={36} className={`${iconClass} mx-auto`} aria-hidden="true" />
      <h1 className="text-lg font-semibold text-[var(--color-text)]">{title}</h1>
      <p className="text-sm text-[var(--color-text-muted)]">{message}</p>
      <div className="pt-2">
        <a
          href="https://pexify.net/"
          className="text-xs text-[var(--color-info)] hover:underline inline-flex items-center gap-1"
        >
          {t.errorStates.homeLink}
          <ArrowRight size={11} aria-hidden="true" />
        </a>
      </div>
    </Card>
  )
}
